'use client'

import { use } from 'react'
import { useApi } from '@excellent/ui/api'
import { lens } from '@excellent/ui/api/contracts/lens'
import { SessionSidebar } from '@excellent/ui/blocks/lens/sessions/replay/session-sidebar'
import { UserTurnCard, AssistantTurnCard } from '@excellent/ui/blocks/lens/sessions/replay/turn-cards'
import { TokenAccumulationChart } from '@excellent/ui/blocks/lens/sessions/replay/token-accumulation-chart'
import { SessionBadges } from '@excellent/ui/blocks/lens/sessions/session-badges'
import { formatCost, formatTokens, formatDuration } from '@lens/lib/decode'
import type { ReplayData, SessionWithFacet } from '@lens/types/claude'
import { StatStrip, type StatStripItem, StatGrid } from "@excellent/ui"
import { Skeleton } from "@excellent/ui"
import { Alert, AlertDescription } from "@excellent/ui"
import { PageHeader } from "@excellent/ui"
import { AlertTriangle } from "@excellent/ui/icons"
import { useSnapshot } from '@lens/lib/use-snapshot'

type ReplayResponse = ReplayData

export default function SessionDetailPage({ params }: { params: Promise<{ id: string }> }) {
 const { id } = use(params)

 // Replay is per-session and inherently needs the JSONL — keep it as its own fetch.
 const { data: replayData, error: replayError, isLoading: replayLoading } =
 useApi(lens.sessionReplay, { params: { id } }) as { data: ReplayResponse | undefined; error: unknown; isLoading: boolean }

 // Session meta comes from the shared snapshot. Falls back to the per-id
 // endpoint only if the snapshot doesn't have it (legacy installs).
 const { snapshot } = useSnapshot()
 const fromSnapshot = snapshot?.sessions.find(s => s.session_id === id)
 const { data: metaData } = useApi(
 lens.sessionById,
 fromSnapshot ? null : { params: { id } },
 ) as { data: { session: SessionWithFacet } | undefined }
 const meta: SessionWithFacet | undefined = fromSnapshot ?? metaData?.session

 const sessionTitle = 'Session'
 const sessionSubtitle = id

 if (replayError) {
 return (
 <div className="flex flex-col min-h-screen">
 <div className="p-6 space-y-4">
 <PageHeader title={sessionTitle} description={sessionSubtitle} />
 <Alert variant="destructive">
 <AlertTriangle className="h-4 w-4" />
 <AlertDescription>Error loading session: {String(replayError)}</AlertDescription>
 </Alert>
 </div>
 </div>
 )
 }

 if (replayLoading || !replayData) {
 return (
 <div className="flex flex-col min-h-screen">
 <div className="p-6 space-y-4">
 <PageHeader title={sessionTitle} description={sessionSubtitle} />
 <StatGrid cols={4}>
 {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
 </StatGrid>
 <div className="space-y-4">
 {Array.from({ length: 5 }).map((_, i) => (
 <Skeleton key={i} className={`h-${i % 2 === 0 ? '16' : '28'} rounded-lg`} />
 ))}
 </div>
 </div>
 </div>
 )
 }

 const replay = replayData

 // Total token counts from replay
 let totalInput = 0, totalOutput = 0, totalCacheRead = 0, totalCacheWrite = 0
 for (const t of replay.turns) {
 if (t.usage) {
 totalInput += t.usage.input_tokens ?? 0
 totalOutput += t.usage.output_tokens ?? 0
 totalCacheWrite += t.usage.cache_creation_input_tokens ?? 0
 totalCacheRead += t.usage.cache_read_input_tokens ?? 0
 }
 }
 const totalTokens = totalInput + totalOutput + totalCacheWrite + totalCacheRead

 // Build tool results map: tool_use_id -> result (from user turns)
 const toolResults = new Map<string, { content: string; is_error: boolean }>()
 for (const t of replay.turns) {
 if (t.type === 'user' && t.tool_results) {
 for (const r of t.tool_results) {
 toolResults.set(r.tool_use_id, { content: r.content, is_error: r.is_error })
 }
 }
 }

 // Build compaction map: index of turn before which a compaction occurred
 const compactionByTurnIndex = new Map(replay.compactions.map(c => [c.turn_index, c]))

 let assistantTurnNum = 0

 const sessionStats: StatStripItem[] = [
 { title: "Turns", value: replay.turns.filter(t => t.type === 'assistant').length.toLocaleString(), description: "Assistant messages" },
 { title: "Tokens", value: formatTokens(totalTokens), description: "Input + output + cache" },
 { title: "Cost", value: formatCost(replay.total_cost ?? 0), description: "Estimated spend" },
 ...(meta ? [{ title: "Duration", value: formatDuration(meta.duration_minutes ?? 0), description: "Session span" }] : []),
 ...(replay.compactions.length > 0 ? [{ title: "Compactions", value: replay.compactions.length.toLocaleString(), description: "Context window events" }] : []),
 ]

 return (
 <div className="flex flex-col min-h-screen">
 <PageHeader title={sessionTitle} description={sessionSubtitle} />
 {/* Stats cards */}
 <div className="py-5">
 <StatStrip variant="standalone" items={sessionStats} />

 {meta && (
 <div className="mt-4 flex flex-wrap gap-2">
 <SessionBadges
 has_compaction={replay.compactions.length > 0}
 uses_task_agent={meta.uses_task_agent}
 uses_mcp={meta.uses_mcp}
 uses_web_search={meta.uses_web_search}
 uses_web_fetch={meta.uses_web_fetch}
 has_thinking={meta.has_thinking}
 />
 </div>
 )}
 </div>

 {/* Two-column layout */}
 <div className="flex flex-1 overflow-hidden">
 {/* Conversation replay */}
 <div className="flex-1 min-w-0 overflow-y-auto px-4 py-6 max-w-6xl">
 {replay.turns.map((turn, i) => {
 const compactionBefore = compactionByTurnIndex.get(i)

 if (turn.type === 'user') {
 return (
 <UserTurnCard
 key={turn.uuid || i}
 turn={turn}
 turnNumber={i + 1}
 compactionBefore={compactionBefore}
 toolResults={toolResults}
 />
 )
 }

 assistantTurnNum++
 return (
 <AssistantTurnCard
 key={turn.uuid || i}
 turn={turn}
 turnNumber={assistantTurnNum}
 compactionBefore={compactionBefore}
 toolResults={toolResults}
 />
 )
 })}
 </div>

 {/* Sidebar */}
 <div className="w-64 shrink-0 overflow-y-auto border-l border-border px-4 py-6">
 <SessionSidebar replay={replay} meta={meta} />
 </div>
 </div>

 {/* Token accumulation chart */}
 <div className="border-t border-border px-4 py-4">
 <TokenAccumulationChart turns={replay.turns} compactions={replay.compactions} />
 </div>
 </div>
 )
}
